import React, { useCallback, useEffect, useRef, useState } from "react";
import { Loader2, AlertCircle, CheckCircle2 } from "lucide-react";
import { apiFetch, resolveApiUrl } from "@/lib/jwt-auth-client";

// ── Types (mirror core/src/domains/managed-agent/jobTracker.ts) ───────────

type JobStatus = "submitted" | "running" | "completed" | "failed";

interface JobStatusResponse {
  jobId: string;
  sessionId: string;
  status: JobStatus;
  taskType: string;
  result?: unknown;
  error?: string;
  createdAt: number;
  updatedAt: number;
}

interface RuntimeImageStatusResponse {
  status: string;
  totalImages?: number;
  completedImages?: number;
}

export interface MiniCardResult {
  agentResponse?: string;
  videoUrl?: string;
  thumbnailUrl?: string;
  imageUrl?: string;
  imageUrls?: string[];
  /** Multi-video batch output — index-aligned with the job's batchItems.
   *  Entries with a missing `videoUrl` denote per-item failures (the agent
   *  emits them so downstream can skip without failing the whole job). */
  videos?: Array<{
    videoUrl?: string;
    thumbnailUrl?: string;
    error?: string;
  }>;
  /** Campaign fields — populated by the now-orphaned ad-publish-google parser;
   *  kept on the result shape so future ads-meta-agent completion parsing can
   *  reuse the same fields. Safe to ignore when not present. */
  campaignId?: string;
  adGroupId?: string;
  adSetId?: string;
  adId?: string;
  campaignStatus?: string;
}

interface RunningAgentMiniCardProps {
  jobId: string;
  dispatchedAt: number;
  statusEndpoint?: "managed-agent" | "runtime-session-image";
  onComplete: (jobId: string, result: MiniCardResult) => void;
  onRuntimeComplete?: (jobId: string) => void;
  onFailed: (jobId: string) => void;
}

// ── Constants ─────────────────────────────────────────────────────────────

const POLL_INTERVAL_MS = 5_000;
const TERMINAL_STATUSES: ReadonlySet<JobStatus> = new Set(["completed", "failed"]);

function normalizeRuntimeImageStatus(data: RuntimeImageStatusResponse): JobStatus {
  const total = typeof data.totalImages === "number" ? data.totalImages : 0;
  const completed = typeof data.completedImages === "number" ? data.completedImages : 0;
  if (
    data.status === "completed" ||
    data.status === "complete" ||
    (total > 0 && completed >= total)
  ) {
    return "completed";
  }
  if (
    data.status === "failed" ||
    data.status === "aborted" ||
    data.status === "cancelled"
  ) {
    return "failed";
  }
  return data.status === "submitted" ? "submitted" : "running";
}

// ── Helpers ───────────────────────────────────────────────────────────────

function formatElapsed(startMs: number): string {
  const seconds = Math.floor((Date.now() - startMs) / 1_000);
  if (seconds < 60) return `${seconds}s`;
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}m ${secs}s`;
}

function extractResult(result: unknown): MiniCardResult {
  if (!result || typeof result !== "object") return {};
  const rec = result as Record<string, unknown>;
  const parsed: MiniCardResult = {};
  if (typeof rec.agentResponse === "string" && rec.agentResponse.trim())
    parsed.agentResponse = rec.agentResponse.trim();
  if (typeof rec.videoUrl === "string" && rec.videoUrl.trim())
    parsed.videoUrl = rec.videoUrl.trim();
  if (typeof rec.thumbnailUrl === "string" && rec.thumbnailUrl.trim())
    parsed.thumbnailUrl = rec.thumbnailUrl.trim();
  if (typeof rec.imageUrl === "string" && rec.imageUrl.trim())
    parsed.imageUrl = rec.imageUrl.trim();
  if (Array.isArray(rec.imageUrls))
    parsed.imageUrls = rec.imageUrls.filter((u): u is string => typeof u === "string" && !!u.trim());
  if (Array.isArray(rec.videos)) {
    const videos: { videoUrl?: string; thumbnailUrl?: string; error?: string }[] = [];
    for (const raw of rec.videos) {
      if (!raw || typeof raw !== "object") continue;
      const v = raw as Record<string, unknown>;
      const entry: { videoUrl?: string; thumbnailUrl?: string; error?: string } = {};
      if (typeof v.videoUrl === "string" && v.videoUrl.trim()) entry.videoUrl = v.videoUrl.trim();
      if (typeof v.thumbnailUrl === "string" && v.thumbnailUrl.trim()) entry.thumbnailUrl = v.thumbnailUrl.trim();
      if (typeof v.error === "string" && v.error.trim()) entry.error = v.error.trim();
      videos.push(entry);
    }
    parsed.videos = videos;
  }
  // Campaign fields — populated by future Meta/GAds completion parsers.
  if (typeof rec.campaignId === "string" && rec.campaignId.trim())
    parsed.campaignId = rec.campaignId.trim();
  if (typeof rec.adGroupId === "string" && rec.adGroupId.trim())
    parsed.adGroupId = rec.adGroupId.trim();
  if (typeof rec.adSetId === "string" && rec.adSetId.trim())
    parsed.adSetId = rec.adSetId.trim();
  if (typeof rec.adId === "string" && rec.adId.trim())
    parsed.adId = rec.adId.trim();
  if (typeof rec.status === "string" && rec.status.trim())
    parsed.campaignStatus = rec.status.trim();
  return parsed;
}

function labelForTaskType(taskType?: string): string {
  switch (taskType) {
    case "image_generation":
      return "Image generation";
    case "text":
      return "Text-to-video";
    case "image":
      return "Image-to-video";
    case "multi-shot":
      return "Multi-shot video";
    case "omni":
      return "Omni video";
    case "effect":
      return "Video effect";
    case "lip-sync":
      return "Lip sync";
    case "motion-control":
      return "Motion control";
    case "video_generation_higgsfield":
      return "Marketing Studio video";
    case "video_generation_higgsfield_cli":
      return "Ecom video";
    case "heygen-video-agent":
      return "Agent V video";
    case "marketing-studio-refs":
      return "Reference prep";
    case "ads-meta-agent":
      return "Meta Ads campaign";
    case "ads-gads-manager":
      return "Google Ads campaign";
    default:
      return "Generation";
  }
}

// ── Component ─────────────────────────────────────────────────────────────

export function RunningAgentMiniCard({
  jobId,
  dispatchedAt,
  statusEndpoint = "managed-agent",
  onComplete,
  onRuntimeComplete,
  onFailed,
}: RunningAgentMiniCardProps) {
  const [status, setStatus] = useState<JobStatus>("submitted");
  const [taskType, setTaskType] = useState<string | undefined>();
  const [elapsed, setElapsed] = useState(() => formatElapsed(dispatchedAt));
  const mountedRef = useRef(true);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const completeFiredRef = useRef(false);

  const fetchStatus = useCallback(async () => {
    try {
      const isRuntimeImageSession = statusEndpoint === "runtime-session-image";
      const res = await apiFetch(
        resolveApiUrl(
          isRuntimeImageSession
            ? `/core/agent/runtime-sessions/${encodeURIComponent(jobId)}/status`
            : `/core/agent/managed-chat/status/${encodeURIComponent(jobId)}`,
        ),
      );
      if (!mountedRef.current) return;
      if (!res.ok) return;
      const raw = await res.json();
      if (!mountedRef.current) return;

      const data: JobStatusResponse = isRuntimeImageSession
        ? {
            jobId,
            sessionId: jobId,
            status: normalizeRuntimeImageStatus(raw as RuntimeImageStatusResponse),
            taskType: "image_generation",
            createdAt: dispatchedAt,
            updatedAt: Date.now(),
          }
        : (raw as JobStatusResponse);

      setStatus(data.status);
      if (data.taskType) setTaskType(data.taskType);

      if (TERMINAL_STATUSES.has(data.status) && !completeFiredRef.current) {
        completeFiredRef.current = true;
        if (intervalRef.current) clearInterval(intervalRef.current);
        if (timerRef.current) clearInterval(timerRef.current);

        if (data.status === "completed") {
          if (isRuntimeImageSession) {
            onRuntimeComplete?.(jobId);
          } else {
            onComplete(jobId, extractResult(data.result));
          }
        } else {
          onFailed(jobId);
        }
      }
    } catch {
      // Silently ignore poll errors — retry on next interval
    }
  }, [dispatchedAt, jobId, onComplete, onFailed, onRuntimeComplete, statusEndpoint]);

  // Start polling + elapsed timer
  useEffect(() => {
    mountedRef.current = true;
    fetchStatus();
    intervalRef.current = setInterval(fetchStatus, POLL_INTERVAL_MS);
    timerRef.current = setInterval(() => {
      setElapsed(formatElapsed(dispatchedAt));
    }, 1_000);

    return () => {
      mountedRef.current = false;
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [fetchStatus, dispatchedAt]);

  const label = labelForTaskType(taskType);

  if (status === "completed") {
    return (
      <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-500/[0.06] border border-emerald-500/15 animate-in fade-in duration-200">
        <CheckCircle2 className="w-3 h-3 text-emerald-500 shrink-0" />
        <span className="text-[10px] font-medium text-emerald-500 truncate">{label} ready</span>
      </div>
    );
  }

  if (status === "failed") {
    return (
      <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-red-500/[0.06] border border-red-500/15 animate-in fade-in duration-200">
        <AlertCircle className="w-3 h-3 text-red-400 shrink-0" />
        <span className="text-[10px] font-medium text-red-400 truncate">{label} failed</span>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-muted/10 border border-border/20 animate-in fade-in duration-200">
      <Loader2 className="w-3 h-3 text-primary/60 shrink-0 animate-spin" />
      <span className="text-[10px] font-medium text-foreground/70 truncate">{label}...</span>
      <span className="text-[10px] text-foreground/40 tabular-nums ml-auto shrink-0">
        {elapsed}
      </span>
    </div>
  );
}
